import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  IconButton,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  List,
  ListItem,
  ListItemText,
  Chip,
  Skeleton,
  Grid2,
  Tooltip,
} from "@mui/material";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import apis from "../utils/apis";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import FitCategoryCell from "../components/common/FitCategoryCell";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import ShortTextIcon from "@mui/icons-material/ShortText";
import { alpha } from "@mui/material/styles";
import { Clear } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ReactComponent as ProfileIcon } from "../components/animations/profilenew.svg";

// Utility function to convert months into a human-readable format
const formatExperience = (totalMonths) => {
  if (totalMonths === null || totalMonths === undefined || totalMonths === "") {
    return "N/A";
  }
  const num = Number(totalMonths);
  if (isNaN(num)) return totalMonths || "N/A";
  const years = Math.floor(num / 12);
  const months = num % 12;
  let experienceText = "";
  if (years > 0) {
    experienceText += `${years} ${years > 1 ? "Years" : "Year"}`;
  }
  if (months > 0) {
    experienceText += ` ${months} ${months > 1 ? "Months" : "Month"}`;
  }
  if (num === 0) {
    return "Fresher/Intern/Trainee";
  }
  return experienceText.trim();
};

const transformExperience = (criteria) => {
  if (!criteria) return criteria;
  // If both minExperience and maxExperience are present (regardless of experience), use only min/max and remove experience
  if (
    criteria.minExperience !== undefined &&
    criteria.minExperience !== null &&
    criteria.maxExperience !== undefined &&
    criteria.maxExperience !== null
  ) {
    return criteria;
  }

  // If experience is present (even if minExperience or maxExperience is present), use only experience and remove min/max
  if (criteria.experience !== undefined && criteria.experience !== null) {
    const experienceInMonths = parseInt(criteria.experience, 10);
    const { minExperience, maxExperience, ...rest } = criteria;
    return {
      ...rest,
      experience: experienceInMonths,
    };
  }

  // If neither, return unchanged
  return criteria;
};

// Function to highlight matching role or location
const highlightMatch = (value, criteriaValue) => {
  if (!value || !criteriaValue) return value;
  const isMatch =
    value.trim().toLowerCase() === criteriaValue.trim().toLowerCase();
  return (
    <span
      style={{
        backgroundColor: isMatch ? "#26801E" : "transparent",
        color: isMatch ? "#fff" : "#FDF9E0",
        fontWeight: isMatch ? "bold" : "normal",
        padding: isMatch ? "2px 6px" : "0",
        borderRadius: isMatch ? "4px" : "0",
      }}
    >
      {value}
    </span>
  );
};

const ResumeRanker = () => {
  const [jdFile, setJdFile] = useState(null); // For uploaded JD file
  const [jdText, setJdText] = useState(""); // For manual JD text input
  const [resumeFiles, setResumeFiles] = useState([]); // For multiple uploaded resumes
  const [pdfFiles, setPdfFiles] = useState([]); // For uploaded PDF resumes
  const [loading, setLoading] = useState(false); // For API call status
  const [rows, setRows] = useState([]); // Data for the table
  const [error, setError] = useState(""); // Error messages
  const [errorStatus, setErrorStatus] = useState(null); // HTTP error status
  const [jdInputType, setJdInputType] = useState("file"); // JD input type: file or text
  const [jobDescriptionContent, setJobDescriptionContent] = useState(""); // For displaying JD content or URL
  const [showJdTextError, setShowJdTextError] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const resultsTableRef = useRef(null); // Ref for the results table
  const navigate = useNavigate(); // Initialize useNavigate

  // Recruiter quota state
  const [recruiterQuota, setRecruiterQuota] = useState(null);

  // Resume Ranker History State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarData, setSidebarData] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarLimit] = useState(20);
  const [sidebarLastId, setSidebarLastId] = useState(null);
  const [sidebarHasMore, setSidebarHasMore] = useState(true);
  const [historySelectedResumeFiles, setHistorySelectedResumeFiles] = useState(
    []
  );
  const [newRanker, setNewRanker] = useState(false); // State to track if a new ranker is being created

  // Add new states for dynamic synonyms updating
  const [pendingSkills, setPendingSkills] = useState(null);
  const [loadingSynonyms, setLoadingSynonyms] = useState(false);

  // New state variables for criteria editing
  const [jdCriteria, setJdCriteria] = useState(null);
  const [fileUrls, setFileUrls] = useState(null);
  const [jobDescriptionUrl, setJobDescriptionUrl] = useState(null);
  const [showCriteriaForm, setShowCriteriaForm] = useState(false);

  // Add missing state variables for historical data to fix ESLint errors
  const [formJdCriteria, setFormJdCriteria] = useState(null);
  const [formJobDescriptionUrl, setFormJobDescriptionUrl] = useState(null);
  const [processingStatus, setProcessingStatus] = useState([]); // Track processing status of resumes
  // Only keep isRoleEdited state to control visibility of synonyms box
  const [isRoleEdited, setIsRoleEdited] = useState(false);
  // Add state for expanding/collapsing skills synonyms
  const [showAllSkillSynonyms, setShowAllSkillSynonyms] = useState(false);

  // Cancel flag and ref
  const cancelProcessingRef = useRef(false);

  // Cancel token source for API calls
  const cancelTokenSource = useRef({});
  const cancelTokenSourceSkillValidity = useRef(null); // Add cancel token source for skill validity

  const [expandedRows, setExpandedRows] = useState({});

  // Define memoized sets for JD skills and synonyms
  const jdSkillSet = useMemo(() => {
    if (!jdCriteria || !Array.isArray(jdCriteria.skills)) return new Set();
    return new Set(
      jdCriteria.skills.map((s) => s.name?.trim().toLowerCase() || "")
    );
  }, [jdCriteria]);

  const jdSkillSynonymsSet = useMemo(() => {
    if (!jdCriteria || !Array.isArray(jdCriteria.skills)) return new Set();
    const synonyms = new Set();
    jdCriteria.skills.forEach((skill) => {
      if (Array.isArray(skill.synonyms)) {
        skill.synonyms.forEach((syn) => {
          if (syn && typeof syn === "string") {
            synonyms.add(syn.trim().toLowerCase());
          }
        });
      }
    });
    // Remove direct JD skills from synonyms set to avoid double-highlighting
    jdCriteria.skills.forEach((skill) => {
      synonyms.delete(skill.name?.trim().toLowerCase() || "");
    });
    return synonyms;
  }, [jdCriteria]);

  useEffect(() => {
    if (rows.length > 0) {
      resultsTableRef.current?.scrollIntoView({ behavior: "smooth" }); // Auto-scroll to the table
    }
  }, [rows]); // Trigger when rows are updated

  const fetchRecruiterQuota = async () => {
    try {
      const response = await apis.GET({ endpoint: "/get-recruiter-quota" });
      if (response.status === 201) setRecruiterQuota(response.data);
    } catch (error) {
      setRecruiterQuota(null);
    }
  };

  const handleJdFileChange = (e) => {
    const fileInput = e.target;
    const selectedFile = fileInput.files[0];
    setShowCriteriaForm(null);

    // Reset the file input value to allow re-selection of the same file
    fileInput.value = "";
    if (selectedFile) {
      setError(""); // Clear any existing error
      setJdFile(selectedFile); // Set the selected file
    }
  };

  const handleJdTextChange = (e) => setJdText(e.target.value);

  const handleResumeChange = (e) => {
    const fileInput = e.target;
    const selectedFiles = Array.from(fileInput.files);
    const maxFileSizeMB = 4.5;
    fileInput.value = "";

    // Only allow one zip file and multiple pdf files
    let zip = null;
    const pdfs = [];
    selectedFiles.forEach((file) => {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxFileSizeMB) {
        setError(`File "${file.name}" exceeds the ${maxFileSizeMB} MB limit.`);
        return;
      }
      if (file.name.toLowerCase().endsWith(".zip")) {
        if (!zip) zip = file; // Only allow one zip file
      } else if (file.name.toLowerCase().endsWith(".pdf")) {
        pdfs.push(file);
      }
    });

    // If only one PDF and no zip, treat as single PDF
    if (pdfs.length === 1 && !zip) {
      setResumeFiles([]);
      setPdfFiles(pdfs);
    } else {
      setResumeFiles(zip ? [zip] : []);
      setPdfFiles(pdfs);
    }
    if (zip || pdfs.length > 0) setError("");
  };

  // Add clear files handler
  const handleClearFiles = () => {
    setResumeFiles([]);
    setPdfFiles([]);
    setError("");
    setHistorySelectedResumeFiles([]);
  };

  // Handle JD input type selection
  const handleJdInputTypeChange = (e) => {
    setJdInputType(e.target.value);
    setJdFile(null);
    setShowCriteriaForm(null);
    setJdCriteria(null);
    if (e.target.value === "text") {
      setJdText(`Role: "Enter role (e.g., Frontend Developer)"
Skills: "Enter skills (e.g., JavaScript, React)"
Location: "Enter location (e.g., Surat, India)"
Experience: "Enter experience (e.g., 5 Years)"`); // Pre-filled text
    } else {
      setJdText(""); // Clear text when switching to file mode
    }
  };

  const secretEncodeDecodeString = useFeatureFlag("secretEncodeDecodeString");
  const staticRandomEncodeDecodeString = useFeatureFlag(
    "staticRandomEncodeDecodeString"
  );

  function DataDecryption(jsonString) {
    const decodedString = atob(jsonString);
    const getOldStringOne = decodedString.replace(
      new RegExp(secretEncodeDecodeString, "g"),
      ""
    );
    const str = staticRandomEncodeDecodeString;
    const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedString, "g");
    const getOldStringTwo = getOldStringOne.replace(regex, '"');
    return JSON.parse(getOldStringTwo);
  }

  // Encryption function
  function DataEncryption(json) {
    const jsonString = JSON.stringify(json);
    const customStringJson =
      secretEncodeDecodeString + jsonString + secretEncodeDecodeString;
    const newCustomStringJson = customStringJson.replace(
      /"/g,
      staticRandomEncodeDecodeString
    );
    const encodedString = btoa(newCustomStringJson);
    return encodedString;
  }

  // Convert file to Base64
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // Remove Base64 header
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // Function to clear error and enable the button
  const handleInputChange = () => {
    setShowCriteriaForm(null);
    setJdCriteria(null);
    setError(""); // Clear any existing error
  };

  // Helper to check if status is allowed (400 or 500)
  const isStatusAllowed = (status) => status === 400 || status === 500;

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowJdTextError(true);
    setJobDescriptionContent("");
    setRows([]);
    setFormJdCriteria(null);
    setNewRanker(true);
    setProcessingStatus([]);
    cancelProcessingRef.current = false;
    cancelTokenSource.current = axios.CancelToken.source();

    // If review/edit form is shown, process resumes directly
    if (showCriteriaForm && jdCriteria && fileUrls && fileUrls.length > 0) {
      await handleProcessResumes();
      return;
    }

    if (
      (jdInputType === "file" && !jdFile) ||
      (jdInputType === "text" && (!jdText || jdText.length <= 5)) ||
      (resumeFiles.length === 0 &&
        pdfFiles.length === 0 &&
        historySelectedResumeFiles.length === 0)
    ) {
      const missingInputs = [];
      if (jdInputType === "file" && !jdFile) missingInputs.push("JD file");
      if (jdInputType === "text" && (!jdText || jdText.length <= 5))
        missingInputs.push("JD text");
      if (
        resumeFiles.length === 0 &&
        pdfFiles.length === 0 &&
        historySelectedResumeFiles.length === 0
      )
        missingInputs.push("resumes");
      setError(`Please provide : ${missingInputs.join(", ")}.`);
      return;
    }

    if (jdInputType === "file" && jdFile) {
      const allowedExtensions = [".pdf", ".txt", ".docx"];
      const fileExtension = jdFile.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        setError("JD file must be a .pdf, .txt, or .docx file.");
        return;
      }
    }

    setError("");
    setErrorStatus(null);
    setLoading(true);
    let updatedSkillsJD = [];

    try {
      const formData = {};
      setError("Uploading Job Description and resumes...");

      if (jdInputType === "file") {
        formData.jobDescriptionFile = await fileToBase64(jdFile);
      } else {
        formData.jobDescription = jdText;
      }

      let uploadFileUrls = [];
      if (
        resumeFiles.length > 0 ||
        pdfFiles.length > 0 ||
        jdInputType === "text"
      ) {
        if (pdfFiles.length === 1 && resumeFiles.length === 0) {
          formData.fileContent = await fileToBase64(pdfFiles[0]);
          formData.fileName = pdfFiles[0].name;
        } else {
          if (resumeFiles.length > 0) {
            formData.zipFileContent = await fileToBase64(resumeFiles[0]);
          }
          if (pdfFiles.length > 0) {
            formData.pdfFiles = await Promise.all(
              pdfFiles.map(async (file) => ({
                fileName: file.name,
                fileContent: await fileToBase64(file),
              }))
            );
          }
        }

        setError("Uploading files to the server...");
        try {
          const uploadFile = async (retryCount = 0) => {
            try {
              return await apis.POST({
                endpoint: "/upload-jd-resume",
                payload: formData,
                options: {
                  type: "json",
                  cancelToken: cancelTokenSource.current.token,
                },
              });
            } catch (err) {
              if (
                (err.response?.status === 502 ||
                  err.response?.status === 400) &&
                retryCount < 1
              ) {
                return uploadFile(retryCount + 1); // Retry once
              }
              throw err; // Throw error after retry
            }
          };

          const uploadResponse = await uploadFile();

          // Prevent showing criteria form if cancelled
          if (cancelProcessingRef.current) {
            setShowCriteriaForm(false);
            setJdCriteria(null);
            setFileUrls(null);
            setLoading(false);
            return;
          }

          const {
            jobDescriptionUrl,
            jdCriteria,
            fileUrls: newFileUrls,
          } = uploadResponse.data;
          var jdCriteriatemp = jdCriteria;
          // --- Call /get-topSkillValidity ONCE for all JD skills ---
          if (jdCriteria && Array.isArray(jdCriteria.skills)) {
            try {
              updatedSkillsJD = await fetchSkillsSynonyms(jdCriteria.skills);
              // Optionally: store or display the result if needed
            } catch (err) {
              console.error("JD Skill validity check failed:", err.message);
            }
          }
          // --- end JD skill validity check ---
          jdCriteriatemp = {
            ...jdCriteriatemp,
            skills: updatedSkillsJD.topPrimarySkill,
          };
          uploadFileUrls = fileUrls;
          formData.jdCriteria = jdCriteriatemp;
          formData.jobDescriptionUrl = jobDescriptionUrl;
          let jdCloneCriteria = jdCriteriatemp;
          setFormJdCriteria(jdCloneCriteria);
          setFormJobDescriptionUrl(jobDescriptionUrl);

          // Combine new uploaded file URLs with history-selected file URLs
          uploadFileUrls = [...newFileUrls, ...historySelectedResumeFiles];

          // Store data and show criteria form
          setJdCriteria(transformExperience(jdCriteriatemp));
          setFileUrls(uploadFileUrls);
          setJobDescriptionUrl(jobDescriptionUrl);
          setShowCriteriaForm(true);
          setPendingSkills(
            jdCriteria.skills?.map((skill) => skill.name).join(", ") || ""
          );
          setError("");
          // fetchJobDescription(jobDescriptionUrl, jdCriteria);
        } catch (err) {
          if (axios.isCancel(err)) {
            setError("Process cancelled. Please try again.");
            setShowCriteriaForm(false); // Hide criteria form if cancelled
            setJdCriteria(null); // Optionally clear criteria
            setFileUrls(null); // Optionally clear fileUrls
          } else {
            console.error(err);
            setLoading(false);
            setError(
              err?.response?.data?.error ||
                "An error occurred while processing. Please try again."
            );
            setErrorStatus(err.response?.status || null);
          }
        } finally {
          setLoading(false);
          fetchRecruiterQuota();
        }
      } else {
        // Handle history-selected files without new uploads
        const allFileUrls = [...historySelectedResumeFiles];
        if (allFileUrls.length === 0) {
          setError("No resumes to process.");
          setLoading(false);
          return;
        }

        // Prevent showing criteria form if cancelled
        if (cancelProcessingRef.current) {
          setShowCriteriaForm(false);
          setJdCriteria(null);
          setFileUrls(null);
          setLoading(false);
          return;
        }

        setFileUrls(allFileUrls);
        setJdCriteria(transformExperience(formJdCriteria)); // Use stored criteria from history
        setJobDescriptionUrl(formJobDescriptionUrl);
        setShowCriteriaForm(true);
        // if (formJobDescriptionUrl) fetchJobDescription(formJobDescriptionUrl, jdCriteria);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        setError("Process cancelled. Please try again.");
        setShowCriteriaForm(false); // Hide criteria form if cancelled
        setJdCriteria(null); // Optionally clear criteria
        setFileUrls(null); // Optionally clear fileUrls
      } else {
        console.error(err);
        setLoading(false);
        setError(
          err?.response?.data?.error ||
            "An error occurred while processing. Please try again."
        );
        setErrorStatus(err.response?.status || null);
      }
    } finally {
      setLoading(false);
      fetchRecruiterQuota();
    }
  };

  // Update the handleProcessResumes function to display error messages below the file name for failed resumes
  const handleProcessResumes = async () => {
    if (!fileUrls || fileUrls.length === 0) {
      setError("No resumes to process.");
      return;
    }

    setLoading(true);
    setError("");
    setProcessingStatus([]); // Reset processing status
    const failedResumes = []; // Track failed resumes

    try {
      setError("Processing resumes...");
      const processPromises = fileUrls.map((fileUrl) => {
        let updatedSkills = [];
        const fileName = fileUrl.split("/").pop();
        setProcessingStatus((prevStatus) => [
          ...prevStatus,
          { fileName, status: "Processing", errorMessage: "" },
        ]);

        const processResume = async (retryCount = 0) => {
          try {
            const response = await apis.POST({
              endpoint: "/process-single-resume",
              payload: { fileUrl },
              options: {
                type: "json",
                cancelToken: cancelTokenSource.current.token,
              },
            });

            // --- Call /get-topSkillValidity ONCE for all resume skills ---
            if (response.data && Array.isArray(response.data.primarySkill)) {
              try {
                updatedSkills = await fetchSkillsSynonyms(
                  response.data.primarySkill
                );
              } catch (err) {
                updatedSkills = response.data.primarySkill;

                // Log error, including 400 Bad Request
                console.error(
                  "Resume Skill validity check failed:",
                  err.message
                );
              }
            }
            // --- end resume skill validity check ---

            setProcessingStatus((prevStatus) =>
              prevStatus.map((status) =>
                status.fileName === fileName
                  ? { ...status, status: "Processed" }
                  : status
              )
            );
            const responseData = {
              ...response.data,
              primarySkill: updatedSkills?.topPrimarySkill,
            };

            // Call /get-updated-form with processed resume data
            const updatedFormResponse = await apis.POST({
              endpoint: "/get-updated-form",
              payload: responseData,
              options: { type: "json" },
            });

            const updatedFormData = {
              ...updatedFormResponse.data.userForm,
              uploadedByRecruiter: true,
              payloadType: "recruiter-result",
            };

            // Call /get-result-recruiter with updated form data and payloadType
            const resultRecruiterResponse = await apis.POST({
              endpoint: "/get-result-recruiter",
              payload: updatedFormData,
              options: { type: "json" },
            });

            const {
              techValue,
              currencySymbol,
              marketSalary,
              uuid,
              convertedIndianSalary,
            } = resultRecruiterResponse.data;

            return {
              success: true,
              data: {
                ...responseData,
                techValue,
                currencySymbol,
                marketSalary,
                uuid,
                convertedMarketSalary: convertedIndianSalary,
              },
              fileUrl,
            };
          } catch (err) {
            const status = err.response?.status;

            if ((status === 502 || status === 504) && retryCount < 2) {
              return processResume(retryCount + 1);
            }
            // Set a specific message for 502 or 504
            const errorMessage =
              status === 502 || status === 504
                ? "Resume processing took over 25 seconds. This may be due to a lengthy resume or a slow network connection. Please try again later."
                : err.response?.data?.error ||
                  "Something went wrong. Please try again.";
            setProcessingStatus((prevStatus) =>
              prevStatus.map((status) =>
                status.fileName === fileName
                  ? { ...status, status: "Failed", errorMessage }
                  : status
              )
            );
            failedResumes.push({ fileName, errorMessage }); // Add failed resume to the list
            return { success: false, error: err, fileUrl };
          }
        };

        return processResume();
      });

      const results = await Promise.all(processPromises);

      if (cancelProcessingRef.current) {
        setError(
          "Process cancelled. Please change JD or select files and proceed further."
        );
        setLoading(false);
        return;
      }

      let processedResumes = [];

      for (const result of results) {
        if (result.success) {
          const updatedData = {
            ...result.data,
            experience:
              typeof result?.data?.experience === "string"
                ? result.data.experience.replace(/[^0-9]/g, "")
                : "12", // Default to 12 months if experience is not available
          };
          processedResumes.push(updatedData);
        } else {
          if (axios.isCancel(result.error)) {
            setError(
              "Process cancelled. Please change JD or select files and proceed further."
            );
          } else {
            console.error(
              `Error processing resume: ${result.fileUrl}`,
              result.error
            );
            // setError(
            //   (prevError) =>
            //     `${prevError}\nFailed to process resume: ${result.fileUrl.split("/").pop()}`
            // );
          }
        }
      }

      // Clean skills before sending to API
      processedResumes = cleanResumeSkills(processedResumes);

      var updatedJdCriteria = [];
      if (jdCriteria.minExperience || jdCriteria.maxExperience) {
        const averageExp =
          (jdCriteria.minExperience + jdCriteria.maxExperience) / 2;
        updatedJdCriteria = { ...jdCriteria, experience: averageExp };
      } else {
        updatedJdCriteria = { ...jdCriteria };
      }

      setError("Ranking resumes...");
      const rankingResponse = await apis.POST({
        endpoint: "/jd-to-resume-ranking",
        payload: {
          jdCriteria: updatedJdCriteria,
          resumes: processedResumes,
          jobDescriptionUrl,
          files: fileUrls,
        },
        options: { type: "json" },
      });

      const parsedData = rankingResponse.data;
      if (parsedData && Array.isArray(parsedData)) {
        const rowsWithIdsAndSkills = parsedData.map((row, index) => ({
          id: index,
          Fit: row.Fit,
          FitPercentage: row.FitPercentage,
          SmartSalary: row.SmartSalary,
          Name: row.Name,
          Location: row.Location,
          Experience: row.Experience,
          Role: row.Role,
          Skills: row.Skills, // <-- Keep as array, not joined string
          Certification: Array.isArray(row.Certification)
            ? row.Certification.map((cert) => cert.name)
            : [],
          UUID: row.UUID,
        }));

        fetchJobDescription(jobDescriptionUrl, jdCriteria);

        setRows(rowsWithIdsAndSkills);
        const hasAnyUUID = rowsWithIdsAndSkills.some((row) => row.UUID);
        setColumnVisibilityModel((prev) => ({ ...prev, action: hasAnyUUID }));
        setError("");
        setShowCriteriaForm(false);
        fetchSidebarData(true);
      } else {
        setError("No data received from the server.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        setError("Process cancelled. Please try again.");
      } else {
        console.error(err);
        setLoading(false);
        setError(err?.response?.data?.error);
        setErrorStatus(err.response?.status || null);
      }
    } finally {
      setLoading(false);
      fetchRecruiterQuota();
    }

    // Display failed resumes below the processing status
    // if (failedResumes.length > 0) {
    //   setError((prevError) =>
    //     `${prevError}\nFailed Resumes:\n${failedResumes.map((resume) => `${resume.fileName}: ${resume.errorMessage}`).join("\n")}`
    //   );
    // }
  };

  // Cancel handler
  const handleCancelProcessing = () => {
    cancelProcessingRef.current = true;
    setLoading(false);
    setError("cancelling current process..."); // Show cancel message
    setShowCriteriaForm(false); // Hide criteria form immediately on cancel
    setJdCriteria(null); // Optionally clear criteria
    setFileUrls(null); // Optionally clear fileUrls
    cancelTokenSource.current.cancel(
      "Process cancelled. Please change JD or select files and proceed further."
    ); // Cancel API calls
  };

  // Fetch Resume Ranker History
  const fetchSidebarData = async (reset = false) => {
    if (sidebarLoading || (!sidebarHasMore && !reset)) return;
    setSidebarLoading(true);
    try {
      const params = {
        limit: sidebarLimit,
        ...(sidebarLastId && !reset ? { lastId: sidebarLastId } : {}),
      };
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/get-resume-ranking-history?${queryString}`;
      const response = await apis.GET({ endpoint });
      if (response.status === 201 && Array.isArray(response.data.data)) {
        const newData = response.data.data;
        setSidebarData(reset ? newData : [...sidebarData, ...newData]);
        setSidebarLastId(response.data.nextLastId || null);
        setSidebarHasMore(!!response.data.hasMore);
      } else {
        setSidebarHasMore(false);
      }
    } catch (e) {
      setSidebarHasMore(false);
    } finally {
      setSidebarLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (sidebarData.length === 0) {
      setSidebarLastId(null);
      fetchSidebarData(true);
    }
    fetchRecruiterQuota();
  }, []);

  // Sidebar list item click handler: prefill JD text and show results
  const handleSidebarItemClick = (item) => {
    setShowCriteriaForm(false); // Hide criteria form when selecting a new history record
    setError(""); // Clear error message
    setErrorStatus(null); // Clear error status
    setLoading(false); // Reset loading state
    setNewRanker(true);
    // Prefill JD text from filters
    if (
      item.filters &&
      Array.isArray(item.filters) &&
      item.filters.length > 0
    ) {
      const filter = item.filters[0];

      // Compose JD text from filter fields
      let experienceText = "";
      if (
        filter.minExperience !== undefined &&
        filter.minExperience !== null &&
        filter.maxExperience !== undefined &&
        filter.maxExperience !== null
      ) {
        // Prefill as "Experience: X to Y Years"
        const minYears = Math.floor(filter.minExperience / 12);
        const minMonths = filter.minExperience % 12;
        const maxYears = Math.floor(filter.maxExperience / 12);
        const maxMonths = filter.maxExperience % 12;
        let minStr = minYears > 0 ? `${minYears} Years` : "";
        if (minMonths > 0) minStr += ` ${minMonths} Months`;
        let maxStr = maxYears > 0 ? `${maxYears} Years` : "";
        if (maxMonths > 0) maxStr += ` ${maxMonths} Months`;
        experienceText = `Experience: ${minStr.trim()} to ${maxStr.trim()}`;
      } else if (
        filter.experience !== undefined &&
        filter.experience !== null
      ) {
        experienceText = `Experience: ${formatExperience(filter.experience)}`;
      } else {
        experienceText = "Experience: Not available";
      }

      // Budget logic
      let budgetText = "";
      const budgetLow = Number(filter.budgetLow);
      const budgetHigh = Number(filter.budgetHigh);
      if (
        !isNaN(budgetLow) &&
        !isNaN(budgetHigh) &&
        budgetLow > 0 &&
        budgetHigh > 0
      ) {
        if (budgetLow === budgetHigh) {
          budgetText = `Budget: ${budgetLow}`;
        } else {
          budgetText = `Budget: ${budgetLow} to ${budgetHigh}`;
        }
      }

      const jdTextPrefill = [
        typeof filter.role === "string"
          ? `Role: ${filter.role}`
          : typeof filter.role?.role === "string"
          ? `Role: ${filter.role.role}`
          : "",
        Array.isArray(filter?.skills) && filter.skills.length
          ? `Skills: ${filter.skills.map((s) => s.name).join(", ")}`
          : "",
        filter.location ? `Location: ${filter.location}` : "",
        experienceText,
        budgetText,
      ]
        .filter(Boolean)
        .join("\n");

      setJdInputType("text");
      setJdText(jdTextPrefill);
      setShowJdTextError(false);
    }
    if (item.results && Array.isArray(item.results)) {
      setFormJdCriteria(item.filters[0]);
      const rowsWithIdsAndSkills = item.results.map((row, index) => ({
        id: index,
        Fit: row.Fit,
        FitPercentage: row.FitPercentage,
        SmartSalary: row?.SmartSalary,
        Name: row.Name,
        Location: row.Location,
        Experience: row.Experience,
        Role: row.Role,
        Skills:
          row.Skills && Array.isArray(row.Skills)
            ? row.Skills // <-- Keep as array
            : [],
        Certification: Array.isArray(row.Certification)
          ? row.Certification.map((cert) => cert.name)
          : [],
        UUID: row.UUID,
      }));
      const jdCriteria = transformExperience(item.filters[0]);
      // Add these before using them in formattedJdText
      const hasMinMax =
        jdCriteria.minExperience !== undefined &&
        jdCriteria.minExperience !== null &&
        jdCriteria.maxExperience !== undefined &&
        jdCriteria.maxExperience !== null;
      const hasExperience =
        jdCriteria.experience !== undefined && jdCriteria.experience !== null;

      const formattedJdText = (
        <div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Role:</strong> {jdCriteria.role.role || jdCriteria?.role}
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Skills:</strong>{" "}
            {jdCriteria.skills.map((skill) => skill.name).join(", ")}
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Location:</strong> {jdCriteria?.location}
          </div>
          {/* Experience display logic */}
          {hasMinMax
            ? // Show as "Experience: X to Y Years"
              (() => {
                const minYears = Math.floor(jdCriteria.minExperience / 12);
                const minMonths = jdCriteria.minExperience % 12;
                const maxYears = Math.floor(jdCriteria.maxExperience / 12);
                const maxMonths = jdCriteria.maxExperience % 12;
                let minStr = minYears > 0 ? `${minYears} Years` : "";
                if (minMonths > 0) minStr += ` ${minMonths} Months`;
                let maxStr = maxYears > 0 ? `${maxYears} Years` : "";
                if (maxMonths > 0) maxStr += ` ${maxMonths} Months`;
                return (
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Experience:</strong>{" "}
                    {`${minStr.trim()} to ${maxStr.trim()}`}
                  </div>
                );
              })()
            : hasExperience
            ? // Show as "Experience: X Years Y Months"
              (() => {
                const years = Math.floor(jdCriteria.experience / 12);
                const months = jdCriteria.experience % 12;
                let expStr = years > 0 ? `${years} Years` : "";
                if (months > 0) expStr += ` ${months} Months`;
                return (
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Experience:</strong> {expStr.trim()}
                  </div>
                );
              })()
            : null}
          {/* Budget display logic */}
          {jdCriteria.budgetLow && jdCriteria.budgetHigh && (
            <div style={{ marginBottom: "10px" }}>
              <strong>Budget:</strong>{" "}
              {jdCriteria.budgetLow === jdCriteria.budgetHigh
                ? jdCriteria.budgetLow
                : `${jdCriteria.budgetLow} to ${jdCriteria.budgetHigh}`}
            </div>
          )}
        </div>
      );
      if (item.JDfileUrl.toLowerCase().endsWith(".pdf")) {
        setJobDescriptionContent(
          <div>
            <a
              href={item.JDfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#FDF9E0", textDecoration: "underline" }}
            >
              View Job Description PDF
            </a>
            <div style={{ marginTop: "10px", color: "#FDF9E0" }}>
              {formattedJdText}
            </div>
          </div>
        );
      } else {
        setJobDescriptionContent(
          <div>
            <a
              href={item.JDfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#FDF9E0", textDecoration: "underline" }}
            >
              View Job Description
            </a>
            <div style={{ marginTop: "10px", color: "#FDF9E0" }}>
              {formattedJdText}
            </div>
          </div>
        );
      }

      // Display the URL as a clickable link and show formatted JD text below

      setRows(rowsWithIdsAndSkills);
      const hasAnyUUID = rowsWithIdsAndSkills.some((row) => row.UUID);
      setColumnVisibilityModel((prev) => ({ ...prev, action: hasAnyUUID }));
    }
    // Prefill JD text from JDfileUrl (fetch and display)

    // Show selected resume files from resumeFileUrls
    if (Array.isArray(item.resumeFileUrls) && item.resumeFileUrls.length > 0) {
      setHistorySelectedResumeFiles(item.resumeFileUrls);
      setResumeFiles([]);
      setPdfFiles([]);
    } else {
      setHistorySelectedResumeFiles([]);
    }

    // Prefill jdCriteria and jobDescriptionUrl for form submission
    // If item.jdCriteria is missing, use item.filters[0] as jdCriteria fallback
    let jdCriteria = transformExperience(
      item.jdCriteria ||
        item.JDCriteria ||
        (Array.isArray(item.filters) && item.filters.length > 0
          ? item.filters[0]
          : null)
    );
    setFormJdCriteria(jdCriteria || null);
    setJdCriteria(jdCriteria || null);
    setFormJobDescriptionUrl(item.JDfileUrl || item.jobDescriptionUrl || null);

    setSidebarOpen(false);
  };

  const fetchJobDescription = async (url, finalJDCriteria) => {
    const jdCriteria = transformExperience(finalJDCriteria);

    // Format JD criteria into a user-friendly layout
    // Show "Experience: X to Y Years" if both min/max, else "Experience: X Years Y Months" if only experience, else "Experience: Not available"
    let experienceBlock = null;
    if (
      jdCriteria.minExperience !== undefined &&
      jdCriteria.minExperience !== null &&
      jdCriteria.maxExperience !== undefined &&
      jdCriteria.maxExperience !== null
    ) {
      const minYears = Math.floor(jdCriteria.minExperience / 12);
      const minMonths = jdCriteria.minExperience % 12;
      const maxYears = Math.floor(jdCriteria.maxExperience / 12);
      const maxMonths = jdCriteria.maxExperience % 12;
      let minStr = minYears > 0 ? `${minYears} Years` : "";
      if (minMonths > 0) minStr += ` ${minMonths} Months`;
      let maxStr = maxYears > 0 ? `${maxYears} Years` : "";
      if (maxMonths > 0) maxStr += ` ${maxMonths} Months`;
      experienceBlock = (
        <div style={{ marginBottom: "10px" }}>
          <strong>Experience:</strong> {`${minStr.trim()} to ${maxStr.trim()}`}
        </div>
      );
    } else if (
      jdCriteria.experience !== undefined &&
      jdCriteria.experience !== null
    ) {
      const years = Math.floor(jdCriteria.experience / 12);
      const months = jdCriteria.experience % 12;
      let expStr = years > 0 ? `${years} Years` : "";
      if (months > 0) expStr += ` ${months} Months`;
      experienceBlock = (
        <div style={{ marginBottom: "10px" }}>
          <strong>Experience:</strong> {expStr.trim()}
        </div>
      );
    } else {
      experienceBlock = (
        <div style={{ marginBottom: "10px" }}>
          <strong>Experience:</strong> Not available
        </div>
      );
    }

    const formattedJdText = (
      <div>
        <div style={{ marginBottom: "10px" }}>
          <strong>Role:</strong>{" "}
          {jdCriteria?.role?.role === ""
            ? "-"
            : jdCriteria?.role?.role || jdCriteria?.role}
        </div>
        <div style={{ marginBottom: "10px" }}>
          <strong>Skills:</strong>{" "}
          {jdCriteria.skills?.map((skill) => skill.name).join(", ")}
        </div>
        <div style={{ marginBottom: "10px" }}>
          <strong>Location:</strong> {jdCriteria?.location}
        </div>
        {experienceBlock}
        {/* Budget display logic */}
        {jdCriteria.budgetLow > 0 && jdCriteria.budgetHigh > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <strong>Budget:</strong>{" "}
            {jdCriteria.budgetLow === jdCriteria.budgetHigh
              ? jdCriteria.budgetLow
              : `${jdCriteria.budgetLow} to ${jdCriteria.budgetHigh}`}
          </div>
        )}
      </div>
    );
    if (url.toLowerCase().endsWith(".pdf")) {
      // Format JD criteria into a user-friendly layout

      // Display the URL as a clickable link and show formatted JD text below
      setJobDescriptionContent(
        <div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FDF9E0", textDecoration: "underline" }}
          >
            View Job Description PDF
          </a>
          <div style={{ marginTop: "10px", color: "#FDF9E0" }}>
            {formattedJdText}
          </div>
        </div>
      );
    } else {
      // Otherwise, fetch the content
      try {
        const response = await axios.get(url, {
          headers: {
            "Content-Type": "text/plain",
          },
        });
        setJobDescriptionContent(
          <div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#FDF9E0", textDecoration: "underline" }}
            >
              View Job Description
            </a>
            <div style={{ marginTop: "10px", color: "#FDF9E0" }}>
              {formattedJdText}
            </div>
          </div>
        );
      } catch (err) {
        console.error("Failed to load Job Description from URL:", err);
        if (err.code === "ERR_NETWORK") {
          setJobDescriptionContent(
            "Network error occurred while loading Job Description."
          );
        } else {
          setJobDescriptionContent("Failed to load Job Description.");
        }
      }
    }
  };

  // Add these handler functions to support file removal
  const handleRemoveResumeFile = (index) => {
    setResumeFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
  };

  const handleRemovePdfFile = (index) => {
    setPdfFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
  };

  const handleRemoveHistoryFile = (index) => {
    setHistorySelectedResumeFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
  };

  // Helper: Convert months to years and months string
  const convertMonthsToYearsAndMonths = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths) || totalMonths < 0) return "";
    if (totalMonths === 0) return "Fresher/Intern/Trainee";
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    let result = "";
    if (years > 0) {
      result += `${years} ${years > 1 ? "years" : "year"}`;
    }
    if (remainingMonths > 0) {
      result += `${years > 0 ? " " : ""}${remainingMonths} ${
        remainingMonths > 1 ? "months" : "month"
      }`;
    }
    return result.trim() || "0 months";
  };

  // Helper: Emoji based on experience level
  const getExperienceEmoji = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths)) return "";
    if (totalMonths === 0) return "🎓 ";
    const years = Math.floor(totalMonths / 12);
    if (years >= 5) return "🌟 ";
    if (years >= 2) return "🔥 ";
    if (years > 0) return "👍 ";
    const remainingMonths = totalMonths % 12;
    if (remainingMonths >= 6) return "✨ ";
    if (remainingMonths > 0) return "😊 ";
    return "";
  };

  // State for column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    SmartSalary: true, // Initially visible
    action: true,
  });

  // Function to check if SmartSalary column is empty
  const isSmartSalaryEmpty = (rows) => {
    return rows.every(
      (row) =>
        row.SmartSalary === null ||
        row.SmartSalary === undefined ||
        row.SmartSalary === ""
    );
  };

  // Update column visibility based on data
  useEffect(() => {
    if (isSmartSalaryEmpty(rows)) {
      setColumnVisibilityModel((prev) => ({
        ...prev,
        SmartSalary: false, // Hide column if all values are empty
      }));
    } else {
      setColumnVisibilityModel((prev) => ({
        ...prev,
        SmartSalary: true, // Show column if any value is non-empty
      }));
    }
  }, [rows]); // Re-run when rows change

  // Table columns
  const columns = [
    {
      field: "Fit",
      headerName: "Fit",
      width: isMobile ? 100 : 150,
      renderCell: (params) => <FitCategoryCell value={params.value} />,
    },
    {
      field: "FitPercentage",
      headerName: "Fit %",
      width: isMobile ? 100 : 150,
    },
    {
      field: "SmartSalary",
      headerName: "₹Smart Salary",
      width: isMobile ? 150 : 200,
    },
    { field: "Name", headerName: "Name", width: isMobile ? 150 : 200 },
    {
      field: "Location",
      headerName: "Location",
      width: isMobile ? 150 : 200,
      renderCell: (params) =>
        highlightMatch(params.value, jdCriteria?.location),
    },
    {
      field: "Experience",
      headerName: "Experience",
      width: isMobile ? 150 : 200,
      renderCell: (params) => formatExperience(params.value),
    },
    {
      field: "Role",
      headerName: "Role",
      width: isMobile ? 150 : 200,
      renderCell: (params) =>
        highlightMatch(
          params.value,
          jdCriteria?.role?.role || jdCriteria?.role
        ),
    },
    {
      field: "Skills",
      headerName: "Skills",
      width: isMobile ? 200 : 500,
      renderCell: (params) => {
        // params.value is the array of skills
        const skillsArr = Array.isArray(params.value) ? params.value : [];

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {skillsArr.map((skill, idx) => {
              const skillName = skill.name || "";
              const skillNameLower = skillName.trim().toLowerCase();
              const isDirectMatch = jdSkillSet.has(skillNameLower);
              const isSynonymMatch =
                !isDirectMatch && jdSkillSynonymsSet.has(skillNameLower);

              return (
                <Chip
                  key={idx}
                  label={skillName}
                  size="small"
                  sx={{
                    backgroundColor:
                      isDirectMatch || isSynonymMatch ? "#26801E" : "#333",
                    color: isDirectMatch || isSynonymMatch ? "#fff" : "#FDF9E0",
                    fontWeight:
                      isDirectMatch || isSynonymMatch ? "bold" : "normal",
                    borderRadius: "4px",
                    height: "auto",
                    "& .MuiChip-label": {
                      padding: "4px 8px",
                      whiteSpace: "normal",
                    },
                  }}
                  title={
                    isDirectMatch || isSynonymMatch
                      ? "JD Skill Match"
                      : undefined
                  }
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "Certification",
      headerName: "Certification",
      width: isMobile ? 150 : 200,
      renderCell: (params) => {
        const certsArr = Array.isArray(params.value) ? params.value : [];
        const rowId = params.row.id;
        const isExpanded = expandedRows[rowId];
        const displayedCerts = isExpanded ? certsArr : certsArr.slice(0, 2);
        const hasMore = certsArr.length > 2;

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            {displayedCerts.map((cert, idx) => (
              <Chip
                key={idx}
                label={cert}
                size="small"
                sx={{
                  backgroundColor: "#333",
                  color: "#FDF9E0",
                  borderRadius: "4px",
                  height: "auto",
                  "& .MuiChip-label": {
                    padding: "4px 8px",
                    whiteSpace: "normal",
                  },
                }}
              />
            ))}
            {hasMore && (
              <IconButton
                size="small"
                onClick={() => {
                  setExpandedRows((prev) => ({
                    ...prev,
                    [rowId]: !isExpanded,
                  }));
                }}
                sx={{ color: "#FDF9E0" }}
              >
                {isExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: isMobile ? 100 : 150,
      renderCell: (params) =>
        params.row.UUID ? (
          <Tooltip title="Public Profile">
            <IconButton
              component="a"
              href={`/candidate-profile/${params.row.UUID}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#FDF9E0",
                backgroundColor: "#26801E",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#26801E",
                },
                height: "40px",
                width: "40px",
              }}
            >
              <div
                style={{
                  height: "30px",
                  width: "30px",
                  borderRadius: "8px",
                }}
              >
                <ProfileIcon />
              </div>
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  const dataGridStyles = {
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#26801E !important",
      color: "#FDF9E0 !important",
      fontWeight: "bold !important",
    },
    "& .MuiDataGrid-container--top [role='row'], & .MuiDataGrid-container--bottom [role='row']":
      { backgroundColor: "transparent !important" },
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": {
      color: "#FDF9E0",
      borderBottom: "1px solid #FDF9E0",
      padding: "12px 10px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      alignItems: "flex-start",
      verticalAlign: "top",
    },
    "& .MuiDataGrid-row": {
      backgroundColor: "rgba(38, 128, 30, 0.1)",
      "&:hover": { backgroundColor: "rgba(38, 128, 30, 0.2)" },
      minHeight: 48,
      transition: "background 0.2s",
    },
    "& .MuiTablePagination-root, & .MuiTablePagination-select, & .MuiTablePagination-actions button":
      { color: "#FDF9E0" },
    "& svg": { fill: "#FDF9E0 !important" },
    "& .MuiDataGrid-selectedRowCount": { color: "#FDF9E0" },
    "& .MuiDataGrid-row--dynamicHeight": {
      minHeight: "48px !important",
    },
  };

  const handleNewRanker = () => {
    setJdFile(null);
    setJdText("");
    setResumeFiles([]);
    setPdfFiles([]);
    setRows([]);
    setError("");
    setErrorStatus(null);
    setJobDescriptionContent("");
    setShowJdTextError(false);
    setShowCriteriaForm(false);
    setFormJdCriteria(null);
    setFormJobDescriptionUrl(null);
    setHistorySelectedResumeFiles([]);
    setNewRanker(false);
    setProcessingStatus([]); // Reset processing status
    setJdCriteria(null);
    // setFailedResumes([]);
  };
  const LoadingSkeleton = () => {
    const rowsNum = 5; // Matches default pageSize for mobile
    return (
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        {[...Array(rowsNum)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start", // Match cell alignItems
              minHeight: "48px", // Match row minHeight
              backgroundColor: "rgba(38, 128, 30, 0.1)", // Match row background
              transition: "background 0.2s", // Match row transition
              "&:hover": {
                backgroundColor: "rgba(38, 128, 30, 0.2)", // Match row hover
              },
              borderBottom: "1px solid #FDF9E0", // Match cell border
            }}
          >
            {columns.map((column) => (
              <Box
                key={column.field}
                sx={{
                  flex: column.flex || 1,
                  width: column.width || "auto",
                  padding: "12px 10px", // Match cell padding
                  whiteSpace: "normal", // Match cell whiteSpace
                  wordBreak: "break-word", // Match cell wordBreak
                  verticalAlign: "top", // Match cell verticalAlign
                }}
              >
                <Skeleton
                  variant="text"
                  animation="wave"
                  sx={{
                    width: "80%",
                    height: "1.5rem",
                    backgroundColor: "rgba(253, 249, 224, 0.2)", // Mimic #FDF9E0 with opacity
                    "&::after": {
                      background:
                        "linear-gradient(90deg, transparent, rgba(253, 249, 224, 0.3), transparent)", // Wave effect
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  // Add cleanup effect for cancel tokens
  useEffect(() => {
    return () => {
      if (cancelTokenSourceSkillValidity.current) {
        cancelTokenSourceSkillValidity.current.cancel("Component unmounted.");
      }
    };
  }, []);

  const fetchSkillsSynonyms = async (skills, cancelToken) => {
    try {
      const payload = { primarySkill: skills };
      const response = await apis.POST({
        endpoint: "/get-skills-synonyms",
        payload: payload,
        cancelToken: cancelToken, // Use the passed cancel token
        options: { type: "json" },
      });
      if (!response.data)
        throw new Error("No data received from skills synonyms API.");
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
      } else {
        throw error; // Rethrow other errors to be caught in the onChange handler
      }
    }
  };

  // Utility to clean skills array in each resume
  function cleanResumeSkills(resumes) {
    return resumes.map((resume) => ({
      ...resume,
      skills: Array.isArray(resume.skills)
        ? resume.skills.map((skill) => ({
            name: skill.name,
            ...(skill.synonyms ? { synonyms: skill.synonyms } : {}),
          }))
        : [],
    }));
  }

  var isTruncated = true;

  // Filter synonyms to exclude those matching the role name (case-insensitive, trimmed)
  const roleName = jdCriteria?.role?.role?.trim()?.toLowerCase() || "";
  const filteredSynonyms = roleName
    ? jdCriteria?.role?.synonyms?.filter(
        (syn) => syn.trim().toLowerCase() !== roleName
      ) || []
    : jdCriteria?.role?.synonyms || [];

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        width: "100%",
        color: "#FFF",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        height: "100%",
        minHeight: "100vh",
      }}
    >
      {/* Horizontal box with sidebar icon (left), center title, and quota counts (right) */}
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "90%" },
          mx: "auto",
          mb: { xs: "55px" },
          mt: { xs: "80px", md: "100px" },
          display: "flex",
          alignItems: "center",
          background: "#18181A",
          borderRadius: 2,
          boxShadow: 2,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          justifyContent: "space-between",
          position: "relative",
          minHeight: 70,
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "row",
          },
          gap: { xs: 2, sm: 6, md: 6, lg: 0 },
        }}
      >
        {/* Sidebar Icon (left) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: { xs: "unset", sm: 1 },
            width: { xs: "100%", sm: "auto" },
            mb: { xs: 1, sm: 0 },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              color: "#FDF9E0",
              backgroundColor: "#231F20",
              borderRadius: "8px",
              boxShadow: 2,
              "&:hover": { backgroundColor: "#333" },
              mr: 2,
            }}
          >
            {isMobile ? (
              <ShortTextIcon fontSize="large" />
            ) : (
              <ViewSidebarIcon fontSize="large" />
            )}
          </IconButton>
          <Typography sx={{ fontWeight: 600, color: "#FDF9E0" }}>
            History
          </Typography>
        </Box>

        {/* Centered Resume Ranker title */}
        <Box
          sx={{
            position: { xs: "static", sm: "absolute" },
            left: { sm: "50%" },
            top: { sm: "50%" },
            transform: { sm: "translate(-50%, -50%)" },
            zIndex: 2,
            pointerEvents: "none",
            minWidth: 200,
            textAlign: "center",
            width: { xs: "100%", sm: "auto" },
            mb: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{
              fontWeight: 700,
              fontFamily: "Kanit",
              color: "#FDF9E0",
              letterSpacing: 1,
            }}
          >
            Resume Ranker (File Based){" "}
          </Typography>
        </Box>
        {/* Quota counts (right) */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            alignItems: "center",
            flexWrap: "wrap",
            flex: { xs: "unset", sm: 1 },
            justifyContent: { xs: "center", sm: "flex-end" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ minWidth: 80, textAlign: "center" }}>
            <Typography sx={{ color: "#aaa", fontSize: 13 }}>
              JD Ranker
            </Typography>
            <Typography
              sx={{ color: "#FDF9E0", fontWeight: 700, fontSize: 18 }}
            >
              {recruiterQuota?.deepSearchCount ?? "-"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 80, textAlign: "center" }}>
            <Typography sx={{ color: "#aaa", fontSize: 13 }}>
              Resume Ranker (File Based){" "}
            </Typography>
            <Typography
              sx={{ color: "#FDF9E0", fontWeight: 700, fontSize: 18 }}
            >
              {recruiterQuota?.resumeRankerCount ?? "-"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 80, textAlign: "center" }}>
            <Typography sx={{ color: "#aaa", fontSize: 13 }}>
              Processed
            </Typography>
            <Typography
              sx={{ color: "#FDF9E0", fontWeight: 700, fontSize: 18 }}
            >
              {recruiterQuota?.processedCount ?? "-"}
            </Typography>
          </Box>
        </Box>
      </Box>
      {newRanker && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <Box>
            <IconButton
              onClick={handleNewRanker}
              sx={{
                color: "#FDF9E0",
                backgroundColor: "#231F20",
                borderRadius: "8px",
                boxShadow: 2,
                "&:hover": { backgroundColor: "#333" },
              }}
            >
              <AddIcon fontSize="large" />
            </IconButton>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, color: "#FDF9E0" }}>
              New Ranker
            </Typography>
          </Box>
        </Box>
      )}

      {/* Sidebar Drawer for Resume Ranker History */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? "80vw" : 340,
            backgroundColor: "#18181A",
            color: "#FDF9E0",
            pt: 2,
            pb: 2,
            borderRight: "1px solid #333",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: "Kanit" }}
          >
            Resume Ranker History
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            sx={{ color: "#FDF9E0" }}
          >
            <Clear />
          </IconButton>
        </Box>
        <Divider sx={{ background: "#333" }} />
        <List
          sx={{
            px: 1,
            py: 0,
            overflowY: "auto",
            maxHeight: "80vh",
            minHeight: 120,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {sidebarData.length === 0 && !sidebarLoading && (
            <ListItem
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 120,
                width: "100%",
              }}
            >
              <ListItemText
                primary="No history found."
                sx={{ textAlign: "center" }}
              />
            </ListItem>
          )}
          {sidebarData.map((item, idx) => {
            // Extract first filter object
            const filter =
              Array.isArray(item.filters) && item.filters.length > 0
                ? item.filters[0]
                : {};
            // Format skills
            const skills =
              filter.skills && Array.isArray(filter.skills)
                ? filter.skills.map((s) => s.name).join(", ")
                : "";
            // Experience display logic
            const hasMinMax =
              filter.minExperience !== undefined &&
              filter.minExperience !== null &&
              filter.maxExperience !== undefined &&
              filter.maxExperience !== null;
            const hasExperience =
              filter.experience !== undefined && filter.experience !== null;

            let experienceDisplay = "";
            if (hasMinMax) {
              // Show as "Experience: X to Y Years"
              const minYears = Math.floor(filter.minExperience / 12);
              const minMonths = filter.minExperience % 12;
              const maxYears = Math.floor(filter.maxExperience / 12);
              const maxMonths = filter.maxExperience % 12;
              let minStr = minYears > 0 ? `${minYears} Years` : "";
              if (minMonths > 0) minStr += ` ${minMonths} Months`;
              let maxStr = maxYears > 0 ? `${maxYears} Years` : "";
              if (maxMonths > 0) maxStr += ` ${maxMonths} Months`;
              experienceDisplay = (
                <>
                  <span style={{ color: "#26801E" }}>Experience:</span>{" "}
                  {`${minStr.trim()} to ${maxStr.trim()}`}
                  <br />
                </>
              );
            } else if (hasExperience) {
              experienceDisplay = (
                <>
                  <span style={{ color: "#26801E" }}>Experience:</span>{" "}
                  {formatExperience(filter.experience)}
                  <br />
                </>
              );
            }
            return (
              <ListItem
                button
                key={item.id || item._id || idx}
                onClick={() => handleSidebarItemClick(item)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: alpha("#26801E", 0.08),
                  "&:hover": { backgroundColor: alpha("#26801E", 0.18) },
                  transition: "background 0.2s",
                  alignItems: "flex-start",
                  p: 2,
                }}
              >
                <ListItemText
                  primary={
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 15,
                          color: "#FDF9E0",
                          mb: 0.5,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        <span style={{ color: "#26801E" }}>Role:</span>{" "}
                        {filter?.role?.role === ""
                          ? "-"
                          : filter?.role?.role ||
                            filter.role?.role ||
                            filter.role ||
                            "-"}
                        <br />
                        <span style={{ color: "#26801E" }}>Skills:</span>{" "}
                        {skills || "-"}
                        <br />
                        <span style={{ color: "#26801E" }}>Location:</span>{" "}
                        {filter.location || "-"}
                        <br />
                        {/* Experience logic */}
                        {experienceDisplay}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#aaa" }}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : ""}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
          {sidebarLoading && (
            <ListItem
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 120,
                width: "100%",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#FDF9E0" }} />
            </ListItem>
          )}
        </List>
        {/* Move Load More button outside the List */}
        {sidebarHasMore && !sidebarLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              borderTop: "1px solid #232323",
              background: "#18181A",
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchSidebarData()}
              sx={{
                color: "#FDF9E0",
                borderColor: "#FDF9E0",
                fontWeight: 600,
                px: 3,
                "&:hover": { borderColor: "#26801E", color: "#26801E" },
              }}
            >
              Load More
            </Button>
          </Box>
        )}
      </Drawer>

      <Box
        sx={{
          border: "2px solid #FDF9E0",
          padding: 4,
          borderRadius: 2,
          // width: "80%",
          maxWidth: 900,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: 6,
          backgroundColor: "rgba(39, 140, 55, 0.1)",
          "@media (max-width:768px)": {
            width: "90%",
            padding: 2,
            marginTop: 10,
          },
        }}
      >
        <Header />
        {/* <div> */}
        <form
          onSubmit={handleSubmit}
          style={{ marginBottom: 20, maxWidth: 900, width: "100%" }}
        >
          <Typography variant="h6" color="#FDF9E0" gutterBottom>
            Choose how to provide the Job Description (JD):
          </Typography>
          <RadioGroup
            row
            value={jdInputType}
            onChange={handleJdInputTypeChange}
            disabled={loading}
            sx={{
              marginBottom: "20px",
              "& .MuiRadio-root": { color: loading ? "#fff" : "#FDF9E0" },
              "& .Mui-checked": { color: loading ? "#ccc" : "#26801E" },
              "& .MuiFormControlLabel-label": {
                color: loading ? "#fff" : "#FDF9E0",
              },
              "& .MuiFormControlLabel-label.Mui-disabled": {
                color: loading ? "#fff" : "#FDF9E0",
              },
            }}
          >
            <FormControlLabel
              value="file"
              control={<Radio disabled={loading} />}
              label="Upload JD File"
              disabled={loading}
            />
            <FormControlLabel
              value="text"
              control={<Radio disabled={loading} />}
              label="Enter JD Text"
              disabled={loading}
            />
          </RadioGroup>

          {jdInputType === "file" && (
            <div style={{ marginBottom: 10, color: "#FDF9E0" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <label>
                  <strong>Upload Job Description (Pdf):</strong>
                </label>
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    ml: "10px",
                    textTransform: "none",
                    borderRadius: "5px",
                    border: "1px solid #FDF9E0",
                    backgroundColor: "transparent",
                    color: loading ? "#fff" : "#FDF9E0",
                    "&:hover": { backgroundColor: "#1E6A16" },
                    "&.Mui-disabled": {
                      color: "#fff",
                      borderColor: "#fff",
                      backgroundColor: "#333",
                    },
                  }}
                  disabled={loading}
                >
                  Choose File
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleJdFileChange}
                    hidden
                    disabled={loading}
                  />
                </Button>
              </Box>
              {jdFile && (
                <Typography
                  sx={{
                    marginTop: "10px",
                    fontSize: "14px",
                    color: "#FDF9E0",
                  }}
                >
                  <strong>Selected File:</strong> {jdFile.name}
                </Typography>
              )}
            </div>
          )}
          {jdInputType === "text" && (
            <div style={{ marginBottom: 10 }}>
              <TextField
                label="Enter Job Description (JD)"
                multiline
                rows={4}
                fullWidth
                value={jdText}
                onChange={(e) => {
                  handleJdTextChange(e);
                  handleInputChange();
                  setShowJdTextError(false);
                }}
                error={
                  showJdTextError && jdText.length > 0 && jdText.length <= 5
                }
                helperText={
                  showJdTextError && jdText.length > 0 && jdText.length <= 5
                    ? "The job description (JD) is too short. Please include skill, location, experience, and role info."
                    : ""
                }
                variant="outlined"
                disabled={loading}
                InputProps={{ readOnly: loading }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiInputBase-input": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiFormHelperText-root": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& label.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: loading ? "#333" : "#231F20",
                    "& fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&:hover fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                  },
                }}
              />
            </div>
          )}
          {showCriteriaForm && jdCriteria && (
            <Box
              sx={{
                my: 4,
                width: "100%",
                maxWidth: 600,
                backgroundColor: "rgba(39, 140, 55, 0.1)",
                padding: 4,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" color="#FDF9E0" gutterBottom>
                Review and Edit JD Criteria
              </Typography>
              <TextField
                label="Role"
                value={jdCriteria.role?.role || ""}
                onChange={(e) => {
                  setJdCriteria({
                    ...jdCriteria,
                    role: { ...jdCriteria.role, role: e.target.value },
                  });
                  setIsRoleEdited(true); // Set to true when user edits Role
                }}
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "#333",
                    "& fieldset": {
                      borderColor: "#fff",
                    },
                    "& input.Mui-disabled": {
                      color: "#FDF9E0",
                      WebkitTextFillColor: "#FDF9E0",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: loading ? "#fff" : "#b5b5b5",
                  },
                  "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiInputBase-input": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiFormHelperText-root": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& label.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: loading ? "#333" : "#231F20",
                    "& fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&:hover fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                  },
                }}
              />
              {/* Show synonyms box only if not edited and synonyms exist */}
              {!isRoleEdited && filteredSynonyms.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #FDF9E0",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#FDF9E0", mb: 1 }}>
                    Role Synonyms:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {filteredSynonyms.map((synonym, index) => (
                      <Chip
                        key={index}
                        label={synonym}
                        size="small"
                        sx={{
                          backgroundColor: alpha("#FFC218", 1),
                          color: "#231F20",
                          border: "1px solid #FDF9E0",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {/* Modified Skills TextField with dynamic synonyms update */}
              <TextField
                label="Skills (comma-separated)"
                value={pendingSkills}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setPendingSkills(newValue);
                  setJdCriteria((prev) => ({
                    ...prev,
                    skills: newValue
                      .split(",")
                      .map((name) => ({ name: name.trim() })),
                  }));
                  setLoadingSynonyms(true);

                  // Cancel any ongoing request
                  if (cancelTokenSourceSkillValidity.current) {
                    cancelTokenSourceSkillValidity.current.cancel(
                      "Operation canceled due to new input."
                    );
                  }
                  // Start new request immediately
                  if (newValue.length > 0) {
                    cancelTokenSourceSkillValidity.current =
                      axios.CancelToken.source();
                    fetchSkillsSynonyms(
                      newValue
                        .split(",")
                        .map((name) => ({ name: name.trim() })),
                      cancelTokenSourceSkillValidity.current.token
                    )
                      .then((updatedSkills) => {
                        setJdCriteria((prev) => ({
                          ...prev,
                          skills: updatedSkills.topPrimarySkill || [],
                        }));
                        setLoadingSynonyms(false);
                      })
                      .catch((error) => {
                        if (!axios.isCancel(error)) {
                          console.error(
                            "Failed to fetch skill synonyms:",
                            error.message
                          );
                        } else {
                          setLoadingSynonyms(false);
                        }
                      });
                  } else {
                    setJdCriteria((prev) => ({ ...prev, skills: [] }));
                  }
                }}
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "#333",
                    "& fieldset": { borderColor: "#fff" },
                    "& input.Mui-disabled": {
                      color: "#FDF9E0",
                      WebkitTextFillColor: "#FDF9E0",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: loading ? "#fff" : "#b5b5b5",
                  },
                  "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiInputBase-input": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiFormHelperText-root": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& label.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: loading ? "#333" : "#231F20",
                    "& fieldset": { borderColor: loading ? "#fff" : "#FDF9E0" },
                    "&:hover fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                  },
                }}
              />
              {jdCriteria.skills && jdCriteria.skills.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #FDF9E0",
                    borderRadius: 2,
                    // backgroundColor: "#231F20",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#FDF9E0", flex: 1 }}
                    >
                      Skills Synonyms:
                    </Typography>
                    {!loadingSynonyms && isTruncated && (
                      <IconButton
                        size="small"
                        onClick={() => setShowAllSkillSynonyms((v) => !v)}
                        sx={{
                          color: "#FDF9E0",
                          ml: 1,
                          p: 0.5,
                          background: "none",
                          "&:hover": { background: "none" },
                        }}
                      >
                        {showAllSkillSynonyms ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    )}
                    {loadingSynonyms && (
                      <CircularProgress
                        size={16}
                        sx={{ color: "#FDF9E0", ml: 1 }}
                      />
                    )}
                  </Box>
                  {loadingSynonyms ? (
                    <Typography variant="body2" sx={{ color: "#FDF9E0" }}>
                      Loading synonyms...
                    </Typography>
                  ) : (
                    (() => {
                      const skillsWithSynonyms = jdCriteria.skills.filter(
                        (skill) =>
                          Array.isArray(skill.synonyms) &&
                          skill.synonyms.length > 0
                      );
                      if (skillsWithSynonyms.length === 0) {
                        return (
                          <Typography variant="body2" sx={{ color: "#FDF9E0" }}>
                            No synonyms available.
                          </Typography>
                        );
                      }
                      const lines = skillsWithSynonyms.map((skill) => ({
                        name: skill.name,
                        synonyms: Array.from(new Set(skill.synonyms)).filter(
                          Boolean
                        ),
                      }));
                      const maxLines = 2;
                      const displayLines = showAllSkillSynonyms
                        ? lines
                        : lines.slice(0, maxLines);
                      isTruncated = lines.length > maxLines;

                      return (
                        <Box sx={{ color: "#FDF9E0", fontSize: "0.95em" }}>
                          {displayLines.map((line, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography
                                component="span"
                                sx={{
                                  fontWeight: 600,
                                  color: "#FDF9E0",
                                  mr: 1,
                                  fontSize: "0.98em",
                                }}
                              >
                                {line.name}:
                              </Typography>
                              {line.synonyms.map((syn, sidx) => (
                                <Chip
                                  key={sidx}
                                  label={syn}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha("#FFC218", 1),
                                    color: "#231F20",
                                    border: "1px solid #FDF9E0",
                                    mr: 0.5,
                                    mb: 0.5,
                                  }}
                                />
                              ))}
                            </Box>
                          ))}
                          {isTruncated && !showAllSkillSynonyms && (
                            <Typography
                              component="span"
                              sx={{ color: "#FDF9E0", fontSize: "1.1em" }}
                            >
                              ...
                            </Typography>
                          )}
                        </Box>
                      );
                    })()
                  )}
                </Box>
              )}
              <TextField
                label="Location"
                value={jdCriteria.location || ""}
                onChange={(e) =>
                  setJdCriteria({ ...jdCriteria, location: e.target.value })
                }
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "#333",
                    "& fieldset": {
                      borderColor: "#fff",
                    },
                    "& input.Mui-disabled": {
                      color: "#FDF9E0",
                      WebkitTextFillColor: "#FDF9E0",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: loading ? "#fff" : "#b5b5b5",
                  },
                  "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiInputBase-input": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiFormHelperText-root": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& label.Mui-focused": {
                    color: loading ? "#fff" : "#FDF9E0",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: loading ? "#333" : "#231F20",
                    "& fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&:hover fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: loading ? "#fff" : "#FDF9E0",
                    },
                  },
                }}
              />
              {/* Experience fields logic */}
              {(() => {
                // Show min/max only if BOTH are present (regardless of experience)
                const hasBothMinMax =
                  jdCriteria.minExperience !== undefined &&
                  jdCriteria.minExperience !== null &&
                  jdCriteria.maxExperience !== undefined &&
                  jdCriteria.maxExperience !== null;

                // Show only experience if it is present and NOT both min and max
                const hasOnlyExperience =
                  jdCriteria.experience !== undefined &&
                  jdCriteria.experience !== null &&
                  !(
                    jdCriteria.minExperience !== undefined &&
                    jdCriteria.minExperience !== null &&
                    jdCriteria.maxExperience !== undefined &&
                    jdCriteria.maxExperience !== null
                  );

                if (hasBothMinMax) {
                  return (
                    <Grid2
                      container
                      spacing={2}
                      sx={{ alignItems: "flex-start", pt: 2 }}
                    >
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Minimum Experience (months)"
                          type="number"
                          value={jdCriteria.minExperience}
                          onChange={(e) =>
                            setJdCriteria({
                              ...jdCriteria,
                              minExperience: parseInt(e.target.value),
                            })
                          }
                          fullWidth
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-root.Mui-disabled": {
                              backgroundColor: "#333",
                              "& fieldset": {
                                borderColor: "#fff",
                              },
                              "& input.Mui-disabled": {
                                color: "#FDF9E0",
                                WebkitTextFillColor: "#FDF9E0",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: loading ? "#fff" : "#b5b5b5",
                            },
                            "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiInputBase-input": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiFormHelperText-root": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& label.Mui-focused": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: loading ? "#333" : "#231F20",
                              "& fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                              "&:hover fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                            },
                          }}
                        />
                        {jdCriteria.minExperience !== undefined &&
                          jdCriteria.minExperience !== null &&
                          jdCriteria.minExperience !== "" && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#4caf50", mt: 1, ml: 1 }}
                            >
                              {getExperienceEmoji(jdCriteria.minExperience)}
                              {convertMonthsToYearsAndMonths(
                                jdCriteria.minExperience
                              )}
                            </Typography>
                          )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Maximum Experience (months)"
                          type="number"
                          value={jdCriteria.maxExperience}
                          onChange={(e) =>
                            setJdCriteria({
                              ...jdCriteria,
                              maxExperience: parseInt(e.target.value),
                            })
                          }
                          fullWidth
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-root.Mui-disabled": {
                              backgroundColor: "#333",
                              "& fieldset": {
                                borderColor: "#fff",
                              },
                              "& input.Mui-disabled": {
                                color: "#FDF9E0",
                                WebkitTextFillColor: "#FDF9E0",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: loading ? "#fff" : "#b5b5b5",
                            },
                            "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiInputBase-input": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiFormHelperText-root": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& label.Mui-focused": {
                              color: loading ? "#fff" : "#FDF9E0",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: loading ? "#333" : "#231F20",
                              "& fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                              "&:hover fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: loading ? "#fff" : "#FDF9E0",
                              },
                            },
                          }}
                        />
                        {jdCriteria.maxExperience !== undefined &&
                          jdCriteria.maxExperience !== null &&
                          jdCriteria.maxExperience !== "" && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#4caf50", mt: 1, ml: 1 }}
                            >
                              {getExperienceEmoji(jdCriteria.maxExperience)}
                              {convertMonthsToYearsAndMonths(
                                jdCriteria.maxExperience
                              )}
                            </Typography>
                          )}
                      </Grid2>
                    </Grid2>
                  );
                } else if (hasOnlyExperience) {
                  return (
                    <>
                      <TextField
                        label="Experience (months)"
                        type="number"
                        value={jdCriteria.experience}
                        onChange={(e) =>
                          setJdCriteria({
                            ...jdCriteria,
                            experience: parseInt(e.target.value),
                          })
                        }
                        fullWidth
                        disabled={loading}
                        sx={{
                          mt: 2,
                          "& .MuiOutlinedInput-root.Mui-disabled": {
                            backgroundColor: "#333",
                            "& fieldset": {
                              borderColor: "#fff",
                            },
                            "& input.Mui-disabled": {
                              color: "#FDF9E0",
                              WebkitTextFillColor: "#FDF9E0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: loading ? "#fff" : "#b5b5b5",
                          },
                          "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: loading ? "#fff" : "#FDF9E0",
                          },
                          "& .MuiInputBase-input": {
                            color: loading ? "#fff" : "#FDF9E0",
                          },
                          "& .MuiFormHelperText-root": {
                            color: loading ? "#fff" : "#FDF9E0",
                          },
                          "& label.Mui-focused": {
                            color: loading ? "#fff" : "#FDF9E0",
                          },
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: loading ? "#333" : "#231F20",
                            "& fieldset": {
                              borderColor: loading ? "#fff" : "#FDF9E0",
                            },
                            "&:hover fieldset": {
                              borderColor: loading ? "#fff" : "#FDF9E0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: loading ? "#fff" : "#FDF9E0",
                            },
                          },
                        }}
                      />
                      {/* Show years/months + emoji below Experience */}
                      {jdCriteria.experience !== undefined &&
                        jdCriteria.experience !== null &&
                        jdCriteria.experience !== "" && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#4caf50", mt: 0.5 }}
                          >
                            {getExperienceEmoji(jdCriteria.experience)}
                            {convertMonthsToYearsAndMonths(
                              jdCriteria.experience
                            )}
                          </Typography>
                        )}
                    </>
                  );
                }
                return null;
              })()}
              <Box>
                <Grid2 container gap={2}>
                  <Grid2 columns={6}>
                    <TextField
                      label="Budget Low"
                      type="number"
                      value={jdCriteria.budgetLow}
                      onChange={(e) =>
                        setJdCriteria({
                          ...jdCriteria,
                          budgetLow: parseInt(e.target.value),
                        })
                      }
                      fullWidth
                      disabled={loading}
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root.Mui-disabled": {
                          backgroundColor: "#333",
                          "& fieldset": { borderColor: "#fff" },
                          "& input.Mui-disabled": {
                            color: "#FDF9E0",
                            WebkitTextFillColor: "#FDF9E0",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: loading ? "#fff" : "#b5b5b5",
                        },
                        "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiInputBase-input": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiFormHelperText-root": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& label.Mui-focused": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: loading ? "#333" : "#231F20",
                          "& fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                          "&:hover fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                        },
                      }}
                    />
                  </Grid2>
                  <Grid2 columns={6}>
                    <TextField
                      label="Budget High"
                      type="number"
                      value={jdCriteria.budgetHigh}
                      onChange={(e) =>
                        setJdCriteria({
                          ...jdCriteria,
                          budgetHigh: parseInt(e.target.value) || 0,
                        })
                      }
                      fullWidth
                      disabled={loading}
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root.Mui-disabled": {
                          backgroundColor: "#333",
                          "& fieldset": { borderColor: "#fff" },
                          "& input.Mui-disabled": {
                            color: "#FDF9E0",
                            WebkitTextFillColor: "#FDF9E0",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: loading ? "#fff" : "#b5b5b5",
                        },
                        "& .MuiInputLabel-shrink": { fontWeight: "bold" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiInputBase-input": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiFormHelperText-root": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& label.Mui-focused": {
                          color: loading ? "#fff" : "#FDF9E0",
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: loading ? "#333" : "#231F20",
                          "& fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                          "&:hover fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: loading ? "#fff" : "#FDF9E0",
                          },
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>
                <Typography variant="caption" sx={{ mt: 1, color: "#FDF9E0" }}>
                  * Budget entered will be considered in Indian Rupees (₹)
                </Typography>
              </Box>
            </Box>
          )}
          <div style={{ marginBottom: 10, color: "#FDF9E0" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <label>
                <strong>Select Resumes (Zip or Pdf):</strong>
              </label>
              <Button
                variant="contained"
                component="label"
                sx={{
                  ml: "10px",
                  textTransform: "none",
                  borderRadius: "5px",
                  border: "1px solid #FDF9E0",
                  backgroundColor: "transparent",
                  color: loading ? "#fff" : "#FDF9E0",
                  "&:hover": { backgroundColor: "#1E6A16" },
                  "&.Mui-disabled": {
                    color: "#fff",
                    borderColor: "#fff",
                    backgroundColor: "#333",
                  },
                }}
                disabled={showCriteriaForm || loading}
              >
                Choose File
                <input
                  type="file"
                  accept=".zip,.pdf"
                  multiple
                  onChange={handleResumeChange}
                  hidden
                  disabled={showCriteriaForm || loading}
                />
              </Button>
            </Box>
            {(resumeFiles.length > 0 ||
              pdfFiles.length > 0 ||
              historySelectedResumeFiles.length > 0) && (
              <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#FDF9E0",
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  }}
                >
                  <strong>Selected Files:</strong>
                </Typography>
                <ul style={{ paddingLeft: { xs: "1rem", sm: "1.5rem" } }}>
                  {resumeFiles.map((file, index) => (
                    <li
                      key={`zip-${index}`}
                      style={{
                        color: "#FDF9E0",
                        display: "flex",
                        alignItems: "center",
                        marginBottom: { xs: 4, sm: 8 },
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          flexGrow: 1,
                          wordBreak: "break-word",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          pr: 1,
                        }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveResumeFile(index)}
                        sx={{
                          color: "red",
                          ml: { xs: 0.5, sm: 1 },
                          p: { xs: 0.5, sm: 1 },
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                          },
                        }}
                        disabled={showCriteriaForm || loading}
                      >
                        <CloseIcon />
                      </IconButton>
                    </li>
                  ))}
                  {pdfFiles.map((file, index) => (
                    <li
                      key={`pdf-${index}`}
                      style={{
                        color: "#FDF9E0",
                        display: "flex",
                        alignItems: "center",
                        marginBottom: { xs: 4, sm: 8 },
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          flexGrow: 1,
                          wordBreak: "break-word",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          pr: 1,
                        }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePdfFile(index)}
                        sx={{
                          color: "red",
                          ml: { xs: 0.5, sm: 1 },
                          p: { xs: 0.5, sm: 1 },
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                          },
                        }}
                        disabled={showCriteriaForm || loading}
                      >
                        <CloseIcon />
                      </IconButton>
                    </li>
                  ))}
                  {historySelectedResumeFiles.map((url, idx) => {
                    const fileName = url.split("/").pop().split("-")[0];
                    return (
                      <li
                        key={`history-${idx}`}
                        style={{
                          color: "#FDF9E0",
                          display: "flex",
                          alignItems: "center",
                          marginBottom: { xs: 4, sm: 8 },
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            flexGrow: 1,
                            wordBreak: "break-word",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            pr: 1,
                          }}
                        >
                          {fileName}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveHistoryFile(idx)}
                          sx={{
                            color: "red",
                            ml: { xs: 0.5, sm: 1 },
                            p: { xs: 0.5, sm: 1 },
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "1rem", sm: "1.25rem" },
                            },
                          }}
                          disabled={showCriteriaForm || loading}
                        >
                          <CloseIcon />
                        </IconButton>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            )}
          </div>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            // Only disable if loading, not on error during review/edit step
            disabled={
              loading ||
              // Only disable for error if not in review/edit mode
              (!showCriteriaForm && error && !isStatusAllowed(errorStatus))
            }
            sx={{
              "&.Mui-disabled": { backgroundColor: "#444", color: "#FDF9E0" },
            }}
          >
            {loading
              ? "Processing..."
              : showCriteriaForm
              ? "Process Resumes"
              : "Upload JD & Resumes"}
          </Button>
          {loading && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelProcessing}
              sx={{
                ml: 2,
                color: "#FDF9E0",
                borderColor: "#FDF9E0",
                "&:hover": { borderColor: "#ff1744", color: "#ff1744" },
              }}
            >
              Cancel
            </Button>
          )}
        </form>

        {loading && !showCriteriaForm && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {/* </div> */}
      </Box>

      {/* Combine Job Description Content and Processing Status Table into a single row with mobile-friendly styling */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "center",
          width: "100%",
          maxWidth: 1200,
          px: { xs: 3, sm: 5, md: 0 },
          marginTop: 4,
          "@media (max-width:768px)": {
            flexDirection: "column",
            gap: 1,
          },
        }}
      >
        {/* Job Description Content */}
        {jobDescriptionContent && (
          <Box
            sx={{
              border: "2px solid #FDF9E0",
              padding: 2,
              borderRadius: 2,
              flex: 1,
              maxWidth: { xs: "100%", md: "50%" },
              backgroundColor: "rgba(39, 140, 55, 0.1)",
              "@media (max-width:768px)": {
                width: "100%",
                padding: 1,
              },
            }}
          >
            <Typography
              variant="h5"
              sx={{ mb: 1, fontFamily: "Kanit", textAlign: "center" }}
            >
              Job Description
            </Typography>
            <Typography>{jobDescriptionContent}</Typography>
          </Box>
        )}

        {/* Processing Status Table */}
        {processingStatus.length > 0 && (
          <Box
            sx={{
              border: "2px solid #FDF9E0",
              padding: 2,
              borderRadius: 2,
              flex: 1,
              maxWidth: { xs: "100%", md: "50%" },
              backgroundColor: "rgba(39, 140, 55, 0.1)",
              "@media (max-width:768px)": {
                width: "100%",
                padding: 1,
              },
            }}
          >
            <Typography
              variant="h5"
              sx={{ mb: 1, fontFamily: "Kanit", textAlign: "center" }}
            >
              Resume Processing Status
            </Typography>
            <List>
              {processingStatus.map((status, index) => {
                const cleanedFileName = decodeURIComponent(
                  status.fileName
                ).replace(/-\d+$/, "");
                let statusIcon;
                let statusColor;

                if (status.status === "Processed") {
                  statusIcon = "✔️";
                  statusColor = "green";
                } else if (status.status === "Failed") {
                  statusIcon = "❌";
                  statusColor = "red";
                } else if (status.status === "Processing") {
                  statusIcon = "⏳"; // Processing icon
                  statusColor = "orange";
                }

                return (
                  <ListItem
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      padding: "4px 8px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <Typography
                        sx={{
                          color: statusColor,
                          fontSize: "1.25rem",
                        }}
                      >
                        {statusIcon}
                      </Typography>
                      <ListItemText
                        primary={cleanedFileName}
                        sx={{ wordBreak: "break-word" }}
                      />
                    </Box>
                    {status.errorMessage && (
                      <Typography
                        sx={{
                          color: "red",
                          fontSize: "0.875rem",
                          marginTop: "4px",
                        }}
                      >
                        {status.errorMessage}
                      </Typography>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>

      {/* Data Table */}
      {processingStatus.length > 0 && loading && (
        <Box
          sx={{
            height: isMobile ? 300 : 400,
            width: isMobile ? "100%" : "90%",
            my: 6,
            display: "contents",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1800,
              p: 2,
              borderRadius: 3,
              mb: 4,
              // background: "#18181A",
              // boxShadow: "0 4px 24px 0 rgba(38,128,30,0.10)",
              overflow: "auto",
              // border: "2px solid #26801E",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                color: "#FDF9E0",
                fontFamily: "Kanit",
                textAlign: "center",
              }}
            >
              Results
            </Typography>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: isMobile ? 5 : 10 },
                },
                sorting: {
                  sortModel: [{ field: "FitPercentage", sort: "desc" }],
                },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={loading}
              slots={{
                loadingOverlay: LoadingSkeleton,
              }}
              sx={dataGridStyles}
              autoHeight
              getRowHeight={() => "auto"} // <-- Add this line for dynamic row height
            />
          </Box>
        </Box>
      )}
      {rows.length > 0 && loading === false && (
        <Box
          sx={{
            height: isMobile ? 300 : 400,
            width: isMobile ? "100%" : "90%",
            my: 6,
            display: "contents",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1800,
              p: 2,
              borderRadius: 3,
              mb: 4,
              // background: "#18181A",
              // boxShadow: "0 4px 24px 0 rgba(38,128,30,0.10)",
              overflow: "auto",
              // border: "2px solid #26801E",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                color: "#FDF9E0",
                fontFamily: "Kanit",
                textAlign: "center",
              }}
            >
              Results
            </Typography>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: isMobile ? 5 : 10 },
                },
                sorting: {
                  sortModel: [{ field: "FitPercentage", sort: "desc" }],
                },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={loading}
              slots={{
                loadingOverlay: LoadingSkeleton,
              }}
              sx={dataGridStyles}
              autoHeight
              getRowHeight={() => "auto"} // <-- Add this line for dynamic row height
            />
          </Box>
        </Box>
      )}
      <Box sx={{ mt: 15 }}>
        <FooterComponent />
      </Box>
    </Box>
  );
};

export default ResumeRanker;
